import React, { useState } from 'react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Grid,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CiSearch } from 'react-icons/ci';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Menu, MenuItem, MenuButton } from '@szhsin/react-menu';
import { UserIcon, DeleteIcon } from 'src/images';
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import SearchNotFound from 'src/components/SearchNotFound';
import { UserListHead } from 'src/sections/@dashboard/user';
import CustomPagination from 'src/components/CustomPagination';
import palette from 'src/theme/palette';
import { badgeService } from 'src/services/badge.service';
import { HiDotsHorizontal } from 'react-icons/hi';

import './../../styles/users.css';
import 'react-toastify/dist/ReactToastify.css';
import { IoClose } from 'react-icons/io5';
import { AiFillQuestionCircle } from 'react-icons/ai';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const TABLE_HEAD = [
  { id: 'name', label: 'Badge Name', alignRight: false },
  { id: 'description', label: 'Description', alignRight: false },
  { id: 'actions', label: '', alignRight: false },
];

export default function Badges() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [selectedBadgeId, setSelectedBadgeId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // Queries
  const {
    data: badgeData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['badges', { page, rowsPerPage, searchText }],
    queryFn: () =>
      badgeService.getBadges({
        page,
        limit: rowsPerPage,
        search: searchText,
      }),
  });

  const handleClose = () => setOpen(false);

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => badgeService.deleteBadge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      setOpen(false);
      setSelectedBadgeId(null);
      toast.success('Badge deleted successfully');
    },
    onError: (error) => {
      console.error('Delete badge error:', error);
      toast.error('Failed to delete badge');
    },
  });

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedBadgeId(id);
    setOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedBadgeId) {
      deleteMutation.mutate(selectedBadgeId);
    }
  };

  if (error) {
    toast.error('Failed to fetch badges');
    return <Typography>Error loading badges</Typography>;
  }

  return (
    <Page title="Badges">
      <Container maxWidth="xl">
        <Card style={{ borderRadius: 16 }}>
          <Grid container spacing={2} style={{ padding: 20 }}>
            <Grid item sm={12} xs={12} md={8} lg={10} xl={10}>
              <div
                className="_login_row search_container"
                style={{ backgroundColor: palette.grey[400], borderRadius: 12 }}
              >
                <CiSearch size={24} color={palette.text.primary} />
                <input
                  type="text"
                  placeholder="Search by badge name"
                  className="_auth_fields searchField"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </Grid>
            <Grid item sm={12} xs={12} md={4} lg={2} xl={2}>
              <Button
                variant="contained"
                style={{ height: 55 }}
                fullWidth
                onClick={() => navigate('/admin/add-new-badge')}
              >
                Add New
              </Button>
            </Grid>
          </Grid>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead headLabel={TABLE_HEAD} />
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : badgeData?.data && badgeData.data.length > 0 ? (
                    badgeData.data.map((badge) => (
                      <TableRow key={badge.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            {badge.imageUrl ? (
                              <img
                                src={badge.imageUrl}
                                alt={badge.name}
                                style={{ width: 48, height: 48, borderRadius: 8 }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 8,
                                  backgroundColor: palette.grey[300],
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {badge.name.charAt(0)}
                              </div>
                            )}
                            <Typography variant="subtitle1" noWrap>
                              {badge.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle1" noWrap>
                            {badge.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <div className="_action_row" style={{ position: 'relative' }}>
                            <Menu
                              menuButton={
                                <MenuButton style={{ backgroundColor: 'transparent', border: 'none' }}>
                                  <HiDotsHorizontal color="var(--grey-500)" fontSize={20} className="_view_loan" />
                                </MenuButton>
                              }
                              menuStyle={{
                                // height: 57,
                                borderRadius: 12,
                                width: 199,
                                boxShadow: '0px 2px 30px #0000000F',
                                marginTop: -40,
                              }}
                            >
                              <MenuItem onClick={() => navigate(`/admin/edit-badge/${badge.id}`)}>
                                <img src={UserIcon} className="_view_icon_loan" />
                                <Typography variant="subtitle1">Edit</Typography>
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  handleDeleteClick(badge.id);
                                  setOpen(true);
                                }}
                              >
                                <img src={DeleteIcon} className="_view_icon_loan" />
                                <Typography variant="subtitle1">Delete</Typography>
                              </MenuItem>
                            </Menu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <SearchNotFound searchQuery={searchText} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          {badgeData && (
            <CustomPagination
              count={badgeData.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </Card>
      </Container>

      <BootstrapDialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: '16px',
            maxWidth: '400px',
            width: '100%',
            disple: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
        }}
      >
        <IoClose
          color={palette.grey[500]}
          size={24}
          style={{ cursor: 'pointer', alignSelf: 'flex-end' }}
          onClick={() => setOpen(false)}
        />
        <AiFillQuestionCircle size={120} color={palette.warning.main} />

        <DialogTitle
          sx={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#111827',
            pb: 1,
            textAlign: 'center',
          }}
        >
          Are You Sure?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6B7280', fontSize: '1rem', textAlign: 'center' }}>
            You want to delete the badge?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: 'column',
            gap: 1,
            pt: 2,
            pb: 1,
            width: '100%',
          }}
        >
          <Button
            variant="contained"
            fullWidth
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending}
            sx={{
              height: 48,
              backgroundColor: '#F63D58',
              '&:hover': {
                backgroundColor: '#E02B47',
              },
              width: '100%',
            }}
          >
            {deleteMutation.isPending ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Yes, Delete'}
          </Button>
        </DialogActions>
        {/* <DialogTitle>Delete Badge</DialogTitle>
        <DialogContent>Are you sure you want to delete this badge?</DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions> */}
      </BootstrapDialog>
    </Page>
  );
}
